// Hooks de Administración · Equipo y accesos (T-jre9). Claves propias
// bajo ["administracion", "equipo", …], como las demás secciones. Cada
// escritura invalida además las claves globales que otras pantallas
// leen: `queryKeys.roster` y `queryKeys.memberships` (la tabla vieja de
// Equipo, el Resumen, la resolución de nombres de actores), la lista de
// invitaciones del Resumen (su fila «Equipo invitado»), y tras un cambio
// de alcance `queryKeys.assignableAssets` (cambia `hasSupervisor`).

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  InvitationBatchCommitRequest,
  MemberScopeUpdateBody,
  PatchMembershipRequest,
} from "@bitacorapro/api-types";
import { queryKeys } from "../../../lib/api/queries";
import { resumenQueryKeys } from "../resumen/queries";
import { equipoApi } from "./api";

export const equipoQueryKeys = {
  invitaciones: ["administracion", "equipo", "invitaciones"] as const,
  memberScopes: ["administracion", "equipo", "member-scopes"] as const,
} as const;

function invalidateRoster(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.roster });
  void qc.invalidateQueries({ queryKey: queryKeys.memberships });
}

/** También la llama la pantalla tras una ronda de invitaciones nuevas:
 *  useCreateTeamInvitation (lib/api/queries.ts) sólo invalida el roster
 *  global, no esta lista ni la del Resumen. */
export function invalidateInvitaciones(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: equipoQueryKeys.invitaciones });
  void qc.invalidateQueries({ queryKey: resumenQueryKeys.invitaciones });
}

// staleTime 0: se entra a la pantalla justo después de invitar o de
// revocar, y la lista tiene que reflejarlo aunque otra mutación no haya
// invalidado esta clave.
export function useInvitacionesEquipo(enabled = true) {
  return useQuery({
    queryKey: equipoQueryKeys.invitaciones,
    queryFn: () => equipoApi.invitaciones(),
    staleTime: 0,
    enabled,
  });
}

// Apagada para un rol sin lectura de roster: la ruta responde 403
// TEAM_ROSTER_READ_REQUIRED y la guarda de App.tsx ya redirige antes.
export function useMemberScopes(enabled = true) {
  return useQuery({
    queryKey: equipoQueryKeys.memberScopes,
    queryFn: () => equipoApi.memberScopes(),
    enabled,
  });
}

export function usePatchMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, body }: { membershipId: string; body: PatchMembershipRequest }) =>
      equipoApi.patchMembership(membershipId, body),
    onSuccess: () => invalidateRoster(qc),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => equipoApi.revokeInvitation(id),
    onSuccess: () => invalidateInvitaciones(qc),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => equipoApi.resendInvitation(id),
    // La fila rota su vencimiento aunque el correo no haya salido.
    onSuccess: () => invalidateInvitaciones(qc),
  });
}

export function useUpdateMemberScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: MemberScopeUpdateBody }) =>
      equipoApi.updateMemberScope(userId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: equipoQueryKeys.memberScopes });
      void qc.invalidateQueries({ queryKey: queryKeys.assignableAssets });
      invalidateRoster(qc);
    },
  });
}

// --- Invitar en lote (T-b7bh) --------------------------------------------

// El preview no escribe nada: es una mutación sólo porque lleva un
// archivo y no se cachea (cada planilla corregida se vuelve a subir).
export function useInvitationBatchPreview() {
  return useMutation({
    mutationFn: (file: File) => equipoApi.batchPreview(file),
  });
}

// Cada fila enviada es una invitación nueva: misma invalidación que una
// ronda de «Invitar persona» (invitaciones pendientes + Resumen + roster
// global), sólo si algo salió.
export function useInvitationBatchCommit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InvitationBatchCommitRequest) => equipoApi.batchCommit(body),
    onSuccess: (result) => {
      if (result.sent.length === 0) return;
      invalidateInvitaciones(qc);
      invalidateRoster(qc);
    },
  });
}
